import CMSWrapper from '@/features/shared/CMSWrapper';
import ContentContainer from '@/features/shared/ContentContainer';
import { withGTM } from '@/features/shared/HOCs';
import { MuiContainer } from '@/features/shared/material-ui';
import { ContentListDocumentMapping } from '@/utils/mappings';
import {
  resolveColorName,
  resolveContentBlocks,
  resolveDocument,
  resolveSpacingSize,
  resolveTheme,
} from '@/utils/resolvers';
import React, { useMemo, useRef } from 'react';
import PressCenterResource from './PressCenterResource';
import { mapTarget, onDatalayer } from './PressCenterResource.datalayer';

const MappedPressCenterResource = ({
  content,
  dynamicContent,
  resources,
  presentation,
  contentButtons,
}: any) => {
  const { topSpacing, bottomSpacing, containerBackgroundColor } = presentation;
  const containerProps = useMemo(
    () => ({
      topSpacing,
      bottomSpacing,
      topBackgroundColor: containerBackgroundColor,
      bottomBackgroundColor: containerBackgroundColor,
    }),
    [bottomSpacing, containerBackgroundColor, topSpacing],
  );

  const theme = useMemo(
    () => resolveTheme('bg-color')({ rawVal: containerBackgroundColor }),
    [containerBackgroundColor],
  );
  const { pressCenterResources = [] } = resources || {};

  return (
    <ContentContainer
      {...containerProps}
      datalayerPosition="press_center_resource"
      datalayerVariant={presentation?.datalayerVariant}
    >
      <MuiContainer>
        <PressCenterResource
          content={content}
          dynamicContent={dynamicContent}
          resources={pressCenterResources}
          theme={theme}
          contentButtons={contentButtons}
        />
      </MuiContainer>
    </ContentContainer>
  );
};

const MappedPressCenterResourceGTM = withGTM({
  Component: MappedPressCenterResource,
  onDatalayer,
  config: { mapTarget },
});

const resolvePressCenterResources = ({ rawVal, page, pageModels }: any) => {
  return (
    rawVal &&
    (Array.isArray(rawVal) || rawVal) &&
    rawVal.map((raw: any) => {
      const { section, sectionTitle } = raw || {};
      const { content: contentRaw, sectionDivider } = section || {};
      const contentList = resolveContentBlocks({
        rawVal: contentRaw,
        page,
        pageModels,
      });

      return {
        content: { contentList, sectionDivider },
        sectionTitle,
      };
    })
  );
};

const resolveLeftRailContent = ({ rawVal, page, pageModels }: any) => {
  return rawVal.map((ref: any) => resolveDocument({ rawVal: ref, page, pageModels }));
};

const PressCenterResourceCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    contentListDocument: ['content', ContentListDocumentMapping],
    pressCenterDocument: [
      'resources',
      {
        pressCenterResources: [null, resolvePressCenterResources],
      },
    ],
    leftRailContent: ['dynamicContent', resolveLeftRailContent],
    componentParameterMap: [
      'presentation',
      {
        'container-background-color': ['containerBackgroundColor', resolveColorName],
        'top-spacing': ['topSpacing', resolveSpacingSize],
        'bottom-spacing': ['bottomSpacing', resolveSpacingSize],
        'variant-label': 'datalayerVariant',
      },
    ],
  });

  const contentButtonNames = useRef({
    contentListDocument: 'content',
    pressCenterDocument: 'resources',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedPressCenterResourceGTM}
      mapping={mapping.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default PressCenterResourceCMSWrapper;
